<script setup>
import { usePaymentsStore } from '@/store/modules/payments';
import { addContentLogAPI } from '@/api/logServiceApi';
import IconArrow07 from '@/components/svg/IconArrow07.vue';
import SmartwelSubscriptionSheets from '@/components/partner/SmartwelSubscriptionSheets.vue';
import SmartwelEntryRow from '@/components/partner/SmartwelEntryRow.vue';
import { useBackLayer } from '@/composables/useAppBack';

const props = defineProps({
  visible: { type: Boolean, default: false },
  isLoggedIn: { type: Boolean, default: false },
  // 포인트 충전 클릭 로그 진입점 라벨(option3). 미지정 시 로그 미발화(기존 동작 유지).
  chargeLogSource: { type: String, default: '' },
});

const emit = defineEmits(['close-menu']);

const route = useRoute();
const router = useRouter();
const paymentsStore = usePaymentsStore();
const { isSmartwelMember, partner } = useMember();
const smartwellPoint = computed(() => partner.value?.totalPoint ?? 0);

// 구독 시트 흐름은 SmartwelSubscriptionSheets 컨테이너에 위임
// 구독 상태는 payments store(GET /subscriptions, status=ACTIVE)로 분기
const sheetsRef = ref(null);
const subscribed = computed(() => paymentsStore.isSubscribed);
const onClickSub = () => {
  if (subscribed.value) sheetsRef.value?.openInfo();
  else sheetsRef.value?.openUpsell();
};
const onClickPoint = () => {
  emit('close-menu');
  router.push('/purchase/exchange');
};

const isTicketDropdownOpen = ref(false);
const isChoiceExpanded = ref(false);
const expandedStates = ref({});

const toggleTicketDropdown = () => {
  isTicketDropdownOpen.value = !isTicketDropdownOpen.value;
};

const toggleExpanded = (typeId) => {
  expandedStates.value[typeId] = !expandedStates.value[typeId];
};
const isExpanded = (typeId) => !!expandedStates.value[typeId];
const hasOptions = (ticket) => ticket?.options && ticket.options.length > 0;
const getTicketCount = (ticket) => {
  if (!ticket) return 0;
  return ticket.totalCount !== undefined
    ? ticket.totalCount
    : (ticket.purchaseCount || 0) + (ticket.eventCount || 0);
};

const filteredTickets = computed(() => {
  if (!paymentsStore.analysisTicketsData) return [];
  return paymentsStore.analysisTicketsData.filter(
    (ticket) => ticket.typeId !== 13 && ticket.typeId !== 14,
  );
});

const choiceTotalCount = computed(() => {
  return paymentsStore.lightTicketCount + paymentsStore.concentratedTicketCount;
});

const choiceSubOptions = computed(() => {
  const options = [];
  const details = paymentsStore.choiceTicketDetails;
  const getCount = (item) => (typeof item === 'object' ? item?.count : item) || 0;
  if (getCount(details.concentrated.day7) > 0)
    options.push({ name: '집중(7일)', count: getCount(details.concentrated.day7) });
  if (getCount(details.concentrated.day30) > 0)
    options.push({ name: '집중(30일)', count: getCount(details.concentrated.day30) });
  if (getCount(details.light.day7) > 0)
    options.push({ name: '라이트(7일)', count: getCount(details.light.day7) });
  if (getCount(details.light.day30) > 0)
    options.push({ name: '라이트(30일)', count: getCount(details.light.day30) });
  return options;
});

const handleClickCharge = () => {
  // 포인트 충전 클릭 로그 (진입점 라벨은 부모가 chargeLogSource 로 지정한 경우에만 발화)
  if (props.chargeLogSource) {
    addContentLogAPI({ contentType: 'S_point_charge_btn_opt3', option3: props.chargeLogSource });
  }
  emit('close-menu');
  router.push({ path: '/purchase/points', query: { next: route.fullPath } });
};

const closeDropdown = () => {
  isTicketDropdownOpen.value = false;
};

useBackLayer({
  id: 'drawer-ticket-dropdown',
  isOpen: isTicketDropdownOpen,
  closeFromBack: closeDropdown,
});

// 메뉴 열릴 때 데이터 갱신, 닫힐 때 드롭다운 초기화
watch(
  () => props.visible,
  (v) => {
    if (v) {
      paymentsStore.fetchTicketCount();
      paymentsStore.getOwnPoint();
      if (isSmartwelMember.value) paymentsStore.fetchActiveSubscriptions();
    } else {
      isTicketDropdownOpen.value = false;
    }
  },
);

defineExpose({ closeDropdown });
</script>

<template>
  <div class="credit-info-row">
    <SmartwelEntryRow
      v-if="isSmartwelMember"
      variant="dropdown-card"
      :point="smartwellPoint"
      :subscribed="subscribed"
      @click-sub="onClickSub"
      @click-point="onClickPoint"
    />
    <div class="credit-info-card credit-info-card--ticket">
      <div class="credit-info-card__label">
        <i class="credit-info-card__icon credit-info-card__icon--ticket"></i>
        <span class="credit-info-card__label-text">분석권</span>
      </div>
      <div class="credit-info-card__bottom">
        <span class="credit-info-card__value">{{ paymentsStore.analysisTicketCount ?? 0 }} 건</span>
        <button
          v-if="isLoggedIn"
          class="credit-info-card__toggle-btn"
          @click.stop="toggleTicketDropdown"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            class="credit-info-card__toggle-arrow"
            :class="{ 'credit-info-card__toggle-arrow--open': isTicketDropdownOpen }"
          >
            <circle cx="8" cy="8" r="7.5" fill="#fff" stroke="#999" />
            <path
              d="M5.5 9.5L8 6.5L10.5 9.5"
              stroke="#333"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
    <div class="credit-info-card credit-info-card--point">
      <div class="credit-info-card__label">
        <i class="credit-info-card__icon credit-info-card__icon--point"></i>
        <span class="credit-info-card__label-text">포인트</span>
      </div>
      <div class="credit-info-card__bottom">
        <span class="credit-info-card__value credit-info-card__value--point"
          >{{ paymentsStore.formattedPoints }} P</span
        >
        <button v-if="isLoggedIn" class="credit-info-card__charge-btn" @click="handleClickCharge">
          충전
        </button>
      </div>
    </div>
    <!-- 분석권 상세 드롭다운 -->
    <div
      v-if="isTicketDropdownOpen && paymentsStore.analysisTicketCount > 0"
      class="ticket-dropdown"
      @click.stop
    >
      <ul class="ticket-dropdown__list">
        <li v-for="ticket in filteredTickets" :key="ticket.typeId" class="ticket-dropdown__item">
          <div
            class="ticket-dropdown__header"
            :class="{ 'ticket-dropdown__header--clickable': hasOptions(ticket) }"
            @click.stop="hasOptions(ticket) && toggleExpanded(ticket.typeId)"
          >
            <div class="ticket-dropdown__left">
              <span class="ticket-dropdown__dot">·</span>
              <span>{{ ticket.typeName }}</span>
            </div>
            <div class="ticket-dropdown__right">
              <span>{{ getTicketCount(ticket) }}건</span>
              <IconArrow07
                v-if="hasOptions(ticket)"
                class="ticket-dropdown__arrow-icon"
                :class="{ 'ticket-dropdown__arrow-icon--open': isExpanded(ticket.typeId) }"
              />
            </div>
          </div>
          <ul v-if="hasOptions(ticket) && isExpanded(ticket.typeId)" class="ticket-dropdown__sub">
            <li v-for="opt in ticket.options" :key="opt.name">
              <span>- {{ opt.name }}</span>
              <span>{{ opt.count }}</span>
            </li>
          </ul>
        </li>
        <li v-if="choiceTotalCount > 0" class="ticket-dropdown__item">
          <div
            class="ticket-dropdown__header"
            :class="{ 'ticket-dropdown__header--clickable': choiceSubOptions.length > 0 }"
            @click.stop="choiceSubOptions.length > 0 && (isChoiceExpanded = !isChoiceExpanded)"
          >
            <div class="ticket-dropdown__left">
              <span class="ticket-dropdown__dot">·</span>
              <span>낭만택일</span>
            </div>
            <div class="ticket-dropdown__right">
              <span>{{ choiceTotalCount }}건</span>
              <IconArrow07
                v-if="choiceSubOptions.length > 0"
                class="ticket-dropdown__arrow-icon"
                :class="{ 'ticket-dropdown__arrow-icon--open': isChoiceExpanded }"
              />
            </div>
          </div>
          <ul v-if="choiceSubOptions.length > 0 && isChoiceExpanded" class="ticket-dropdown__sub">
            <li v-for="opt in choiceSubOptions" :key="opt.name">
              <span>- {{ opt.name }}</span>
              <span>{{ opt.count }}</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <div
      v-if="isTicketDropdownOpen && !paymentsStore.analysisTicketCount"
      class="ticket-dropdown ticket-dropdown--empty"
      @click.stop
    >
      <p>보유중인 분석권이 없습니다.</p>
    </div>

    <SmartwelSubscriptionSheets v-if="isSmartwelMember" ref="sheetsRef" />
  </div>
</template>

<style lang="scss">
.mypage-menu-dropdown .login-msg-box .credit-info-row {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.mypage-menu-dropdown .login-msg-box .credit-info-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  padding: 10px 12px;
  border-radius: 12px;
  min-height: 60px;

  &--point {
    flex: 1;
    background: #fff8f5;
  }

  &--ticket {
    width: 84px;
    flex-shrink: 0;
    background: #fffbe2;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #333;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;

    &-text {
      margin-bottom: -1px;
    }
  }

  &__icon {
    display: block;
    width: 14px;
    height: 14px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    &--point {
      background-image: url('@manseImages/point.svg');
    }

    &--ticket {
      background-image: url('@manseImages/ticket-icon.svg');
    }
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__value {
    color: #333;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;

    &--point {
      color: #eb4c10;
    }
  }

  &__charge-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    background: #eb4c10;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
  }

  &__toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__toggle-arrow {
    transition: transform 0.2s;
    transform: rotate(180deg);

    &--open {
      transform: rotate(0deg);
    }
  }
}

.ticket-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  padding: 10px 12px;
  margin-top: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: creditInfoSlideDown 0.15s ease-out;

  &--empty {
    p {
      color: #757575;
      font-size: 12px;
      line-height: 18px;
      margin: 0;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    & + & {
      border-top: 1px solid #f5f5f5;
      margin-top: 4px;
      padding-top: 4px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 12px;
    color: #333;

    &--clickable {
      cursor: pointer;
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;

    span:last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
  }

  &__dot {
    color: #999;
    font-size: 14px;
  }

  &__arrow-icon {
    width: 12px;
    height: 12px;
    transition: transform 0.2s;
    transform: rotate(180deg);

    &--open {
      transform: rotate(0deg);
    }
  }

  &__sub {
    list-style: none;
    margin: 0;
    padding: 2px 0 2px 14px;

    li {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      font-size: 11px;
      color: #757575;
      padding: 2px 0;

      span:first-child {
        flex: 1;
        min-width: 0;
        white-space: nowrap; /* 고정폭 메뉴라 가로 확장 불가 → 한 줄 유지, 넘치면 말줄임 */
        overflow: hidden;
        text-overflow: ellipsis;
      }

      span:last-child {
        flex-shrink: 0;
      }
    }
  }
}

@keyframes creditInfoSlideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
